import { Monitor, Moon, Sun } from 'lucide-react';
import { useRef, type KeyboardEvent } from 'react';
import { setColorMode, useColorMode, type ColorMode } from '../lib/color-mode.js';
import { cn, MenuChoices, MenuLabel } from './ui.js';

const MODES: readonly { value: ColorMode; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'auto', label: 'Match device', Icon: Monitor },
];

/** Light, Dark or Match device, as three small buttons: for a page with no menu. As a radio
 * group, it is one stop for Tab, and the arrow keys move the choice. */
export function ColorModeSwitch() {
  const { mode } = useColorMode();
  const group = useRef<HTMLDivElement>(null);
  const move = (event: KeyboardEvent, by: number) => {
    event.preventDefault();
    const at = MODES.findIndex((m) => m.value === mode);
    const next = MODES[(at + by + MODES.length) % MODES.length]!;
    setColorMode(next.value);
    group.current?.querySelector<HTMLElement>(`[data-mode="${next.value}"]`)?.focus();
  };
  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label="Color mode"
      className="inline-flex rounded-lg border border-slate-200 p-0.5"
      onKeyDown={(event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') move(event, 1);
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') move(event, -1);
      }}
    >
      {MODES.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          data-mode={value}
          aria-checked={mode === value}
          aria-label={label}
          title={label}
          tabIndex={mode === value ? 0 : -1}
          onClick={() => setColorMode(value)}
          className={cn(
            'rounded-md p-1.5 text-slate-500 hover:text-slate-800',
            mode === value && 'bg-slate-100 text-slate-900',
          )}
        >
          <Icon className="size-4" aria-hidden />
        </button>
      ))}
    </div>
  );
}

/** The same choice as one button that steps through them, for a narrow header. */
export function ColorModeButton() {
  const { mode } = useColorMode();
  const at = MODES.findIndex((m) => m.value === mode);
  const current = MODES[at] ?? MODES[2]!;
  const next = MODES[(at + 1) % MODES.length]!;
  return (
    <button
      type="button"
      onClick={() => setColorMode(next.value)}
      aria-label={`Color mode: ${current.label}. Change to ${next.label}`}
      title={current.label}
      className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-slate-800"
    >
      <current.Icon className="size-4" aria-hidden />
    </button>
  );
}

/** The same choice in a menu, under its own heading. */
export function ColorModeMenu() {
  const { mode } = useColorMode();
  return (
    <>
      <MenuLabel>Appearance</MenuLabel>
      <MenuChoices
        label="Color mode"
        value={mode}
        onChange={setColorMode}
        options={MODES.map(({ value, label, Icon }) => ({
          value,
          label,
          icon: <Icon className="size-4" aria-hidden />,
        }))}
      />
    </>
  );
}
